/**
 * 个人页要两边的数据：
 *
 *   account   昵称、头像、注册时间       —— 它是 player 表的写者
 *   gateway   余额、打码量、VIP、局数     —— game-server 才是钱包的单写者
 *
 * **不让 account 去代理 game-server 的数据**：那等于多一条要维护的链路，
 * 而且它会把"其中一边挂了"掩盖成"整个页面挂了"。
 * 两边各拉各的，哪边挂了页面就少那一块。
 */
import { AuthError } from '../auth/api.ts';

export interface AccountProfile {
  uid: string;
  nickname: string | null;
  avatar: string | null;
  status: string;
  createdAt: string | null;
  presets: string[];
  /** 未读站内信数。**跟着资料一起来** —— 它只是一个数字，不值得一次往返。 */
  mailUnread?: number;
  /** 还能自己改几次昵称（注册时系统给的随机名不算） */
  renamesLeft?: number;
  renamesMax?: number;
}

export interface Wager {
  turnover: number;
  target: number;
  /** target 是 0 表示**没有打码要求**，不是"进度 0%" */
  required: boolean;
  done: boolean;
}

export interface VipState {
  level: number;
  dailyFreeSpins: number;
  depositDiscount: number;
  depositTotal: number;
  nextLevel: number | null;
  nextAt: number | null;
  ratio: number;
  claimedToday: boolean;
  /** 当前等级今天**有没有**可领的东西。0 级的每日免费局是 0，所以是 false。 */
  hasReward: boolean;
  /**
   * 现在能不能领 = 有东西可领 且 今天还没领。
   *
   * **服务端算好的，前端不自己拼。** 按 `!claimedToday && dailyFreeSpins > 0`
   * 拼的话，以后加第二种可领的奖励，这条规则就悄悄过期了 ——
   * 按钮还是灰的，而后端已经有东西可发。
   */
  canClaim: boolean;
  freeSpins: number;
  today: string;
}

export interface GameProfile {
  uid: string;
  bal: number;
  // 币种三件套。**都从服务端来** —— 一个部署一个币种，前端不维护对照表。
  cur: string;   // ISO 代码，如 BRL
  sym: string;   // 符号，如 R$
  dec: number;   // 小数位
  rounds: number;
  wager: Wager;
  vip: VipState;
}

/** 一笔提现记录。 */
export interface WithdrawalItem {
  id: number;
  /** 从钱包扣掉的数。**手续费含在里面。** */
  amount: number;
  fee: number;
  /**
   * 实际到账 = amount - fee。**服务端算好的。**
   *
   * 前端拿当前费率去减历史单的话会算错 —— 那一笔收的是提交当时的费率，
   * 而玩家可能已经升级了。
   */
  net: number;
  /** pending 待审核 / approved 已通过 / rejected 已驳回（钱已退回） */
  status: string;
  ts: number | null;
  reviewedTs: number | null;
  note: string | null;
}

/**
 * 手续费规则。**规则，不是某一笔的手续费** —— 界面要在玩家还在填数字的
 * 时候就显示"实际到账"，那时还没有"一笔"。
 *
 * 真正收多少以提交时服务端算的为准（`withdraw` 的响应里带 `fee`）。
 * 这里算出来的只是预览。
 */
export interface FeeRule {
  level: number;
  /** 0.02 = 2%。0 且 min 也是 0 = 这一级免手续费 */
  rate: number;
  min: number;
  /** 0 = 不封顶 */
  max: number;
  version: string;
}

/** 按规则算这一笔的手续费。**和服务端 fees.fee_of 同一个公式。** */
export function feeOf(rule: FeeRule | undefined, amount: number): number {
  if (!rule || (rule.rate <= 0 && rule.min <= 0)) return 0;
  if (!(amount > 0)) return 0;
  let f = Math.max(amount * rule.rate, rule.min);
  if (rule.max > 0) f = Math.min(f, rule.max);
  // 服务端按币种精度四舍五入（fees.CENT）。这里跟着走，
  // 不然预览会比实际收的差一分，而那一分只有在提交之后才被发现。
  return Math.round(f * 100) / 100;
}

/**
 * 提现面板要的一整坨。**一次拿全** —— 分三个请求的话面板会抖三次，
 * 而且其中一个失败就是半个面板空着。
 */
export interface WithdrawState {
  bal: number;
  vipLevel: number;
  dailyLimit: number;
  dailyUsed: number;
  dailyLeft: number;
  minWithdraw: number;
  today: string;
  hasIdentity: boolean;
  /** 还差多少打码量。0 = 没有要求 / 已达标 */
  wagerLeft: number;
  /** **能提多少，服务端算好的**（余额和当日剩余额度取小者）——
   *  前端自己算的话，算错了就是让人填一个必然被拒的数字。 */
  maxNow: number;
  fee: FeeRule;
  /** 从哪一级起免手续费。null = 这一级已经免了 */
  nextFeeFree: number | null;
  items: WithdrawalItem[];
}

/** 一档 VIP 的权益。**服务端下发，前端不存一份。** */
export interface VipLevelConfig {
  level: number;
  /** 到这一级需要的累计有效充值 */
  minDeposit: number;
  dailyFreeSpins: number;
  /** 0.03 = 充值多送 3% */
  depositDiscount: number;
  dailyWithdrawLimit: number;
}

/**
 * 整张等级表。
 *
 * **前端不维护这张表。** 抄一份到前端的话，运营改了档位就要发一次前端版本，
 * 而在那之前玩家看到的权益和实际发的对不上 —— 那是最难解释的一类客诉。
 */
export interface VipConfig {
  version: string;
  minWithdraw: number;
  levels: VipLevelConfig[];
}

/** 领站内信附件的结果。余额和打码目标是领完之后的 */
export interface MailClaimResult {
  mailId: number;
  coins: number;
  wager: number;
  bal: number;
  turnoverTarget: number;
  /** 领过了又点了一次：原样回上次的结果，钱没再进 */
  replayed: boolean;
}

/** 打游戏那条链路（gateway → game-server）。和 AuthApi 分开 —— 它们是两个服务。 */
export class GameApi {
  #url: string;

  constructor(url: string) {
    this.#url = url;
  }

  async call<T>(op: string, token: string, params: Record<string, unknown> = {}): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.#url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ op, ...params }),
      });
    } catch (e) {
      throw new AuthError('NETWORK', String((e as Error)?.message ?? e), 0);
    }
    const text = await res.text();
    let parsed: Record<string, unknown> | null = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { /* 见下 */ }
    if (!res.ok) {
      throw new AuthError(String(parsed?.code ?? `HTTP_${res.status}`),
                          String(parsed?.msg ?? ''), res.status,
                          parsed?.detail as Record<string, string> | undefined);
    }
    if (parsed === null) throw new AuthError('BAD_RESPONSE', '', res.status);
    return parsed as unknown as T;
  }

  profile(token: string) {
    return this.call<GameProfile>('profile', token);
  }

  vipConfig(token: string) {
    return this.call<VipConfig>('vip_config', token);
  }

  withdrawals(token: string) {
    return this.call<WithdrawState>('withdrawals', token);
  }

  /**
   * 申请提现。`reqId` 是**客户端生成的幂等键** ——
   * 响应在路上丢了、用户又点了一次，是重复提交最常见的来源。
   * 重发同一个号不会变成两笔（后端在 req_id 上有唯一约束）。
   */
  withdraw(token: string, reqId: string, amount: number) {
    return this.call<{ id: number; amount: number; fee: number; net: number;
                       bal: number; status: string; replayed: boolean;
                       ts: number | null }>(
      'withdraw', token, { reqId, amount });
  }

  /**
   * 领一封信的附件。**只发信的 id** —— 金币和打码量由服务端从那封信里读，
   * 客户端传什么都不认（和下单只发商品 code 同一条规矩）。
   */
  claimMail(token: string, mailId: number) {
    return this.call<MailClaimResult>('mail_claim', token, { mailId });
  }

  claimVip(token: string) {
    return this.call<{ level: number; reward: { freeSpins: number }; freeSpins: number }>(
      'vip_claim', token);
  }
}

/** 头像 ref → 能放进 `<img src>` 的地址。内置的用内联 SVG 画，不发请求。 */
export function avatarUrl(ref: string | null, accountUrl: string): string | null {
  if (!ref || ref.startsWith('preset:')) return null;
  return accountUrl.replace(/\/*$/, '/') + 'avatars/' + encodeURIComponent(ref);
}
